import { useState, useEffect, ReactNode } from "react";
import type { CSSProperties } from "react";
import { AuthContext, AuthContextType } from "./AuthContext";
import { requestAccessToken, oauthConfigAvailable, initializeAuth, hasValidToken } from "./auth";

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const requiresAuth = oauthConfigAvailable();

  useEffect(() => {
    const checkAuthStatus = async () => {
      if (!requiresAuth) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        initializeAuth();

        if (hasValidToken()) {
          setIsAuthenticated(true);
          setIsLoading(false);
          return;
        }

        await requestAccessToken();
        setIsAuthenticated(true);
      } catch (err) {
        console.log("No valid token found, user needs to authenticate");
        setIsAuthenticated(false);
        setError(err instanceof Error ? err.message : "Authentication failed");

        if (requiresAuth) {
          try {
            setIsLoading(true);
            setError(null);
            await requestAccessToken();
            setIsAuthenticated(true);
          } catch (loginErr) {
            console.log("Immediate login failed:", loginErr);
            const errorMessage = loginErr instanceof Error ? loginErr.message : "Login failed";
            setError(errorMessage);
            setIsAuthenticated(false);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, [requiresAuth]);

  const contextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    error,
    requiresAuth,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      {isLoading && (
        <div className="loading-overlay" style={overlayStyle}>
          <div>Authenticating...</div>
        </div>
      )}
      {error && !isLoading && (
        <div className="error-overlay" style={overlayStyle}>
          <div>Authentication failed. Please try refreshing the page.</div>
          <div style={{ fontSize: "0.8rem", color: "#8c8b7c", marginTop: "4px" }}>{error}</div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

const overlayStyle: CSSProperties = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 1000,
  padding: "1.5em 2em",
  borderRadius: "8px",
  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
  border: "1px solid #e0e4ef",
};
